import { fg } from '@atlaskit/platform-feature-flags';
import type { Perimeter } from '@atlassian/atl-context';

import { CategoryStorageType } from '../../../types';
import { isFlatPatternFileFormatEnabled } from '../feature-flagging';

/**
 * @deprecated
 */
export enum Sources {
	BIFROST = 'bifrost',
	CLOUDFRONT_S3 = 'cloudfrontS3',
}

export enum FileType {
	COOKIE = 'COOKIE',
	LOCAL_STORAGE = 'LOCAL_STORAGE',
	SESSION_STORAGE = 'SESSION_STORAGE',
	INDEXED_DB = 'INDEXED_DB',
}

const generateResourcePath = (
	perimeter: string,
	storageType: string,
	useFlatFormat: boolean = false,
): string => {
	// TEMPORARY: gate that switches category file fetching from the production CDN
	// to the staging CDN for testing. Remove once COOK-587 is tested.
	const useStagingCategories = fg('platform_bsc_categories_from_staging');

	const baseBifrostCommercialPath = useStagingCategories
		? 'https://atlassian-cookies.stg-east.frontend.public.atl-paas.net/'
		: 'https://atlassian-cookies.prod-east.frontend.public.atl-paas.net/';
	const baseBifrostFedrampPath = useStagingCategories
		? 'https://atlassian-cookies.stg.frontend.cdn.atlassian-us-gov-mod.com/'
		: 'https://atlassian-cookies.frontend.cdn.atlassian-us-gov-mod.com/';

	const fileName = useFlatFormat
		? `categories_v2_${storageType}.json`
		: `categories_${storageType}.json`;

	switch (perimeter) {
		case 'fedramp-moderate':
			return `${baseBifrostFedrampPath}${fileName}`;
		case 'commercial':
			return `${baseBifrostCommercialPath}${fileName}`;
		default:
			return `${baseBifrostCommercialPath}${fileName}`;
	}
};

export const getCommercialCategoryCacheUrlLookup = (
	useFlatFormat: boolean = false,
): Record<CategoryStorageType, string> => {
	return {
		[CategoryStorageType.Cookies]: generateResourcePath(
			'commercial',
			FileType.COOKIE,
			useFlatFormat,
		),
		[CategoryStorageType.LocalStorage]: generateResourcePath(
			'commercial',
			FileType.LOCAL_STORAGE,
			useFlatFormat,
		),
		[CategoryStorageType.SessionStorage]: generateResourcePath(
			'commercial',
			FileType.SESSION_STORAGE,
			useFlatFormat,
		),
		[CategoryStorageType.IndexedDB]: generateResourcePath(
			'commercial',
			FileType.INDEXED_DB,
			useFlatFormat,
		),
	};
};

export const getCategoryCacheUrlPerimeterLookup = (
	useFlatFormat: boolean = false,
): Partial<Record<Perimeter, Record<CategoryStorageType, string>>> => {
	return {
		commercial: getCommercialCategoryCacheUrlLookup(useFlatFormat),
		'fedramp-moderate': {
			[CategoryStorageType.Cookies]: generateResourcePath(
				'fedramp-moderate',
				FileType.COOKIE,
				useFlatFormat,
			),
			[CategoryStorageType.LocalStorage]: generateResourcePath(
				'fedramp-moderate',
				FileType.LOCAL_STORAGE,
				useFlatFormat,
			),
			[CategoryStorageType.SessionStorage]: generateResourcePath(
				'fedramp-moderate',
				FileType.SESSION_STORAGE,
				useFlatFormat,
			),
			[CategoryStorageType.IndexedDB]: generateResourcePath(
				'fedramp-moderate',
				FileType.INDEXED_DB,
				useFlatFormat,
			),
		},
	};
};

/**
 * To be leveraged by SSR products
 * @param source no longer used
 * @param perimeter the perimeter (commercial, fedramp-moderate)
 * @param storageType the storage type
 * @returns the category cache URL
 */
export const getCategoryCacheUrlPerimeterSSR = (
	source: Sources,
	perimeter: Perimeter,
	storageType: FileType,
): string => {
	const useFlatFormat = isFlatPatternFileFormatEnabled();
	return generateResourcePath(perimeter, storageType, useFlatFormat);
};
